const HOLDERS = 'Mike Mroczka & Nil Mamano'

/**
 * How much the notice claims — and whether there is a notice at all.
 *
 * - `all` — everything on the surface is ours. True of editorial content and
 *   of problems BCTCI both owns and posed.
 * - `none` — render nothing. On a problem that originated elsewhere there is
 *   no claim worth printing: asserting rights over "our" statement, examples
 *   and test cases invites the argument that they resemble the originals, and
 *   winning that argument is worth less than never starting it (Mike, LG-409).
 *   Silence is the position, not a weaker claim.
 */
export type CopyrightScope = 'all' | 'none'

/**
 * `scope` is required on purpose. A default would have to be one of the two,
 * and the safe one is not the common one: `all` is right for editorial panes
 * and wrong for any problem we did not pose, so defaulting to it means a new
 * surface overclaims by saying nothing. Naming it at every call site makes the
 * blanket claim a decision rather than an omission.
 *
 * Rendering `null` rather than leaving the choice to callers keeps the whole
 * question — claim, wording, and whether to speak — in one module. A caller
 * that had to decide whether to mount this at all would be a second place the
 * rule lives.
 */
export function Copyright({ scope }: { scope: CopyrightScope }) {
  // Written as "only for `all`", not "never for `none`". The scope reaches the
  // learner's panel over the wire from `problems.getSources`, so a client can
  // meet a value its own union does not have — a tab left open across a deploy,
  // or a bundle newer than the API answering it. Testing for the claim means an
  // unrecognised scope falls silent; testing against `none` would make it print
  // the blanket claim, which is the one outcome that cannot be walked back.
  if (scope !== 'all') return null

  const years = `2025-${new Date().getFullYear()}`

  return (
    <p className="text-muted-foreground mt-8 pt-4 text-xs">
      Copyright &copy; {years} {HOLDERS}. All rights reserved.
    </p>
  )
}
